import React, { memo, useEffect, useMemo, useState } from 'react';
import { Options, Point } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/modules/boost';
import CustomEvents from 'highcharts-custom-events';
import { HighchartsReact } from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { getChartEvents, getCreditsStyle } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import Tooltip from '@components-clean/core/tooltip';
import { HC_COLORS_STANDARD, OpraColours } from '@interfaces/opra-chart.interface';
import { HighchartsWithEvents, StackedChartData } from '@interfaces-clean/panels.interface';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelTheme } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';

CustomEvents(Highcharts);

interface StackedChartProps {
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    panelData: StackedChartData;
    showLegend?: boolean;
    theme?: PanelTheme;
    yAxisTitle?: string;
    xAxisTitle?: string;
    showWaterMark?: boolean;
    title?: string;
    usePercentStacking?: boolean;
    customOptions?: Options;
    customHeight?: number;
}

interface StackedChartPropsWithChartType extends StackedChartProps {
    chartType: string;
}

const Chart = ({
    chartRef,
    panelData,
    showLegend,
    theme = PanelTheme.NONE,
    yAxisTitle,
    xAxisTitle,
    showWaterMark = false,
    title,
    chartType,
    usePercentStacking = false,
    customOptions,
    customHeight,
}: StackedChartPropsWithChartType) => {
    const { series, xCategories } = panelData;
    const [options, setOptions] = useState<HighchartsWithEvents | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [targetElement, setTargetElement] = useState<EventTarget | null>(null);
    // We need to set the height in both the chart and the container, otherwise we see the chart try and be bigger than it should be before
    // transitioning to the specified height, which looks a bit odd.
    const CHART_HEIGHT_PIXELS = 316;
    const { t } = useTranslation();

    function getItemWidth() {
        const longestName =
            series?.reduce((a, b) => ((a?.name?.length || 0) > (b?.name?.length || 0) ? a : b))?.name?.length || 0;
        const maxCharacterWidth = 11;
        const legendPointWith = 15;

        const minWidth = Math.max(longestName * maxCharacterWidth + legendPointWith, 100);

        return Math.min(minWidth, 200);
    }

    // Can't always rely on the total for highcharts, as it seems to not calculate it once there are more
    // than 50 columns and the chart is boosted. I'm hesistant to optionally only calculate when boosted as I'm
    // not 100% confident on when Highcharts fails to calculate the total.
    const xAxisTotals = useMemo(() => {
        return series && xCategories
            ? series.reduce<Record<number, number>>((acc, curr) => {
                  if (curr.values && curr.values.length === xCategories.length) {
                      xCategories.forEach((_, index) => {
                          if (acc[index] === undefined) acc[index] = 0;
                          acc[index] += curr.values![index];
                      });
                  }
                  return acc;
              }, {})
            : {};
    }, [series, xCategories]);

    const scrollablePlotAreaMinimumHeight = useMemo(() => {
        const heightFactor = (customHeight ?? CHART_HEIGHT_PIXELS) / 316;
        const categoryScaleFactor = Math.ceil(xCategories!.length / (4 * heightFactor)) / 2;
        return (customHeight ?? CHART_HEIGHT_PIXELS) * categoryScaleFactor;
    }, [xCategories, customHeight]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOptions({
            colors: customOptions?.colors ?? getThemeColours(theme),
            exporting: {
                chartOptions: {
                    title: { text: title, style: { fontWeight: 'semi-bold' } },
                    credits: { enabled: false },
                    chart: {
                        height: 600,
                        width: 600,
                    },
                    legend: {
                        maxHeight: 400,
                        width: 550,
                        itemWidth: undefined,
                        layout: 'horizontal',
                    },
                },
            },
            chart: {
                type: chartType,
                height: customHeight ?? CHART_HEIGHT_PIXELS,
                zooming: {
                    type: 'xy',
                },
                ...(panelData.hasScrollablePlotArea && {
                    scrollablePlotArea: {
                        minHeight: scrollablePlotAreaMinimumHeight,
                    },
                }),
                events: getChartEvents(showWaterMark, false, title, undefined, {
                    fullScreenOptions: {
                        legend: {
                            maxHeight: 140,
                            itemWidth: 400,
                        },
                        xAxis: {
                            labels: {
                                style: {
                                    width: 200,
                                },
                            },
                        },
                        ...(panelData.hasScrollablePlotArea && {
                            tooltip: {
                                outside: false,
                            },
                        }),
                    },
                    printOptions: {
                        xAxis: {
                            labels: {
                                style: {
                                    width: 200,
                                },
                            },
                        },
                        ...(panelData.hasScrollablePlotArea && {
                            chart: {
                                scrollablePlotArea: {
                                    minHeight: -1,
                                },
                            },
                        }),
                    },
                    defaultOptions: {
                        legend: {
                            maxHeight: 70,
                            itemWidth: getItemWidth(),
                        },
                        xAxis: {
                            labels: {
                                style: {
                                    textOverflow: 'ellipsis',
                                },
                            },
                        },
                        ...(panelData.hasScrollablePlotArea && {
                            chart: {
                                scrollablePlotArea: {
                                    minHeight: scrollablePlotAreaMinimumHeight,
                                },
                                height: CHART_HEIGHT_PIXELS,
                            },
                            tooltip: {
                                outside: true,
                            },
                        }),
                    },
                }),
            },
            xAxis: {
                categories: xCategories,
                title: {
                    text: xAxisTitle || null,
                },
                labels: {
                    style: {
                        textOverflow: 'ellipsis',
                    },
                    events: {
                        mouseover(e: Event) {
                            if ((e as MouseEvent).target) {
                                setTargetElement((e as MouseEvent).target);
                                setOpen(true);
                            }
                        },
                        mouseout() {
                            setTargetElement(null);
                            setOpen(false);
                        },
                    },
                },
            },
            plotOptions: {
                column: {
                    ...(customOptions?.plotOptions?.column as Highcharts.PlotColumnOptions),
                    stacking:
                        (customOptions?.plotOptions?.column as Highcharts.PlotColumnOptions)?.stacking ??
                        (usePercentStacking ? 'percent' : 'normal'),
                },
                bar: {
                    ...(customOptions?.plotOptions?.bar as Highcharts.PlotBarOptions),
                    stacking:
                        (customOptions?.plotOptions?.bar as Highcharts.PlotBarOptions)?.stacking ??
                        (usePercentStacking ? 'percent' : 'normal'),
                },
                line: {
                    linecap: 'square',
                    color: styles['line-colour'],
                },
            },
            tooltip: {
                formatter() {
                    const self: Point = this;

                    if (panelData.tooltipFormatter) {
                        return panelData.tooltipFormatter(self, xAxisTotals);
                    }

                    return ReactDOMServer.renderToStaticMarkup(
                        <div>
                            <b>{self.key}</b> <br />
                            {self.series.name}
                            <br />
                            Value: {FormatNumberForChart(self.y as number)} <br />
                            Percent: {FormatNumberForChart(((self.y as number) / xAxisTotals[self.x]) * 100)}
                        </div>,
                    );
                },
            },
            legend: {
                enabled: showLegend,
                symbolHeight: 9,
                symbolWidth: 10,
                symbolRadius: 2,
                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    textOverflow: 'ellipsis',
                },
                maxHeight: 70,
                itemWidth: getItemWidth(),
            },
            series: series?.map((x) => ({
                data: x.values?.map((val) => {
                    // This fixes an issue when the chart is boosted.
                    // It would display the wrong tooltips for the 0 values. Some odd rendering behaviour for 0 value entries
                    // A 0 value series wont display a column, so treat it as null and set nullInteraction = false
                    if (val === 0) return null;
                    return val;
                }),
                type: 'column' as const,
                stacking:
                    (customOptions?.plotOptions?.column as Highcharts.PlotColumnOptions)?.stacking ??
                    (usePercentStacking ? 'percent' : 'normal'),
                borderRadius: {
                    radius: 0,
                    scope: 'stack',
                    where: 'end',
                },
                name: x.name,
                nullInteraction: false,
            })),
            yAxis: {
                title: {
                    text: yAxisTitle,
                },
                ...customOptions?.yAxis,
            },
            credits: showWaterMark ? getCreditsStyle(`${t('cm:waterMarkMainPanelText')}`) : { enabled: false },
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panelData, showLegend, theme, customOptions]);

    if (options === null) return <Loading />;

    return (
        <Tooltip anchorElement="pointer" open={open} targetElement={targetElement} position="left">
            <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />{' '}
        </Tooltip>
    );
};

export const StackedBarChart = memo((stackedChartProps: StackedChartProps) => (
    <Chart {...stackedChartProps} chartType="bar" />
));

export const StackedColumnChart = memo((stackedChartProps: StackedChartProps) => (
    <Chart {...stackedChartProps} chartType="column" />
));
